import React, { useState, useEffect, useMemo } from 'react';
import { XMarkIcon, PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import { LeagueOutput } from '../generated-api';
import { DefaultService } from '../generated-api';
import { resolveLeagueIcon } from '../utils/leagueIcon';

interface LeagueSelectionGridProps {
  leagues: LeagueOutput[];
  selectedLeagueIds: string[];
  onToggleLeagueSelection: (leagueId: string) => void;
  title?: string;
  description?: string;
  showSelectedCount?: boolean;
  disabled?: boolean;
  defaultExpanded?: boolean;
}

const LeagueSelectionGrid: React.FC<LeagueSelectionGridProps> = ({
  leagues,
  selectedLeagueIds,
  onToggleLeagueSelection,
  title = "Leagues",
  description = "Choose which leagues this play session should count towards. Players not in selected leagues won't have their league ratings affected.",
  showSelectedCount = true,
  disabled = false,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [leaguePlayCounts, setLeaguePlayCounts] = useState<Record<string, number>>({});
  const [loadingPlayCounts, setLoadingPlayCounts] = useState(false);

  // Fetch plays and calculate league play counts
  useEffect(() => {
    const fetchPlayCounts = async () => {
      try {
        setLoadingPlayCounts(true);
        const plays = await DefaultService.getPlaysPlaysGet({ limit: 200 });
        
        // Count plays per league
        const counts: Record<string, number> = {};
        plays.forEach(play => {
          play.leagueIds?.forEach(leagueId => {
            counts[leagueId] = (counts[leagueId] || 0) + 1;
          });
        });
        
        setLeaguePlayCounts(counts);
      } catch (error) {
        console.error('Error fetching play counts:', error);
      } finally {
        setLoadingPlayCounts(false);
      }
    };

    // Only fetch if we need to show suggestions (only Global selected)
    const globalLeague = leagues.find(l => l.isGlobal);
    const onlyGlobalSelected = selectedLeagueIds.length === 1 && 
                               globalLeague && 
                               selectedLeagueIds.includes(globalLeague.id);
    
    if (onlyGlobalSelected && !isExpanded) {
      fetchPlayCounts();
    }
  }, [selectedLeagueIds, leagues, isExpanded]);

  const selectableLeagues = leagues.filter(l => !l.isGameSpecificLeague);

  const sortedLeagues = selectableLeagues.sort((a, b) => {
    // Global league comes first
    if (a.isGlobal) return -1;
    if (b.isGlobal) return 1;
    
    // Unranked league comes second
    if (a.name === 'Unranked') return -1;
    if (b.name === 'Unranked') return 1;
    
    // All other leagues sorted alphabetically
    return a.name.localeCompare(b.name);
  });

  const selectedLeagues = sortedLeagues.filter(league => 
    selectedLeagueIds.includes(league.id)
  );

  // Check if only Global league is selected
  const globalLeague = leagues.find(l => l.isGlobal);
  const onlyGlobalSelected = selectedLeagueIds.length === 1 && 
                             globalLeague && 
                             selectedLeagueIds.includes(globalLeague.id);

  // Get top 3 leagues by play count (excluding Global and Unranked)
  const suggestedLeagues = useMemo(() => {
    if (!onlyGlobalSelected || loadingPlayCounts) return [];
    
    const nonSystemLeagues = sortedLeagues.filter(league => 
      !league.isGlobal && league.name !== 'Unranked'
    );
    
    return nonSystemLeagues
      .map(league => ({
        league,
        playCount: leaguePlayCounts[league.id] || 0
      }))
      .sort((a, b) => b.playCount - a.playCount)
      .slice(0, 3)
      .map(item => item.league);
  }, [onlyGlobalSelected, sortedLeagues, leaguePlayCounts, loadingPlayCounts]);

  const handleRemoveLeague = (e: React.MouseEvent, leagueId: string) => {
    e.stopPropagation();
    onToggleLeagueSelection(leagueId);
  };

  return (
    <div className="bg-surface shadow rounded-lg p-6">
      {/* Header */}
      <div className="mb-4">
        <h2 className="text-lg font-medium text-heading">{title}</h2>
        {isExpanded && description && (
          <p className="text-sm text-body mt-1">
            {description}
          </p>
        )}
      </div>

      {leagues.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-muted mb-2">Loading leagues...</p>
        </div>
      ) : (
        <>
          {/* Collapsed State: Selected Leagues as Compact Chips */}
          {!isExpanded && (
            <div className="space-y-3">
              {selectedLeagues.length === 0 ? (
                <div className="flex items-center gap-2">
                  <p className="text-sm text-muted">No leagues selected</p>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="ml-auto p-3 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    aria-label="Select leagues"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex flex-wrap gap-2 flex-1 min-w-0">
                    {selectedLeagues.map((league) => (
                      <div
                        key={league.id}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg border border-primary-500 bg-primary-50 text-primary-900"
                      >
                        <div className="text-2xl flex-shrink-0">
                          {resolveLeagueIcon(league.icon, league.isGlobal ? '🌍' : '🏆')}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">
                            {league.name}
                          </div>
                        </div>
                        {!disabled && (
                          <button
                            data-track="league_select_remove"
                            type="button"
                            onClick={(e) => handleRemoveLeague(e, league.id)}
                            className="flex-shrink-0 p-0.5 text-primary-700 hover:text-primary-900 hover:bg-primary-100 rounded transition-colors"
                            aria-label={`Remove ${league.name}`}
                          >
                            <XMarkIcon className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    ))}
                    
                    {/* Show suggestions inline when only Global is selected */}
                    {onlyGlobalSelected && suggestedLeagues.length > 0 && suggestedLeagues.map((league) => {
                      const playCount = leaguePlayCounts[league.id] || 0;
                      return (
                        <button
                          data-track="league_select_option"
                          key={league.id}
                          type="button"
                          onClick={() => onToggleLeagueSelection(league.id)}
                          disabled={disabled}
                          className="flex items-center gap-2 px-3 py-2 rounded-lg border border-edge bg-surface-base text-disabled opacity-60 hover:opacity-80 hover:border-edge-input hover:bg-surface-active transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="text-2xl flex-shrink-0 opacity-75">
                            {resolveLeagueIcon(league.icon)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-xs font-medium truncate">
                              {league.name}
                            </div>
                          </div>
                          {playCount > 0 && (
                            <div className="text-xs text-disabled">
                              ({playCount})
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setIsExpanded(true)}
                    className="flex-shrink-0 p-3 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                    aria-label="Change leagues"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Expanded State: Compact Icon-First Grid */}
          {isExpanded && (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
              {sortedLeagues.map((league) => {
                const isSelected = selectedLeagueIds.includes(league.id);
                const isUnranked = league.name === 'Unranked';
                const unrankedSelected = selectedLeagueIds.some(id => {
                  const l = leagues.find(le => le.id === id);
                  return l?.name === 'Unranked';
                });
                // Disable if: component is disabled, OR
                // - Unranked is selected and this league is not Unranked and not already selected
                // Note: Unranked itself is always clickable (it will deselect others when clicked)
                const isDisabled = disabled || 
                                  (unrankedSelected && !isUnranked && !isSelected);

                return (
                  <button
                    data-track="league_select_option"
                    key={league.id}
                    type="button"
                    disabled={isDisabled}
                    onClick={() => onToggleLeagueSelection(league.id)}
                    className={`relative flex flex-col items-center justify-center p-3 rounded-lg border transition-all min-h-[88px] ${
                      isDisabled
                        ? 'opacity-50 cursor-not-allowed border-edge bg-surface-base'
                        : isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-900 shadow-sm scale-105'
                        : 'border-edge bg-surface text-body hover:bg-surface-hover hover:border-edge-input hover:shadow-sm'
                    }`}
                  >
                    {/* Icon */}
                    <div className="text-3xl md:text-4xl mb-1">
                      {resolveLeagueIcon(league.icon, league.isGlobal ? '🌍' : '🏆')}
                    </div>
                    
                    {/* League Name */}
                    <div className="text-xs font-medium text-center truncate w-full px-1">
                      {league.name}
                    </div>

                    {/* Global/Unranked Badge */}
                    {(league.isGlobal || isUnranked) && (
                      <div className="absolute top-1 right-1">
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-yellow-100 text-yellow-800">
                          {league.isGlobal ? 'G' : 'U'}
                        </span>
                      </div>
                    )}

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div className="absolute top-1 left-1">
                        <div className="w-4 h-4 bg-primary-500 rounded-full flex items-center justify-center">
                          <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* Selected count and confirm button in expanded state */}
          {isExpanded && (
            <div className="mt-4 flex items-center justify-between">
              {showSelectedCount && (
                <div className="text-sm text-body">
                  Selected: {selectedLeagueIds.length} league{selectedLeagueIds.length !== 1 ? 's' : ''}
                </div>
              )}
              <button
                type="button"
                onClick={() => setIsExpanded(false)}
                className="ml-auto p-3 rounded-full bg-primary-600 text-white hover:bg-primary-700 transition-colors"
                aria-label="Confirm selection"
              >
                <CheckIcon className="h-5 w-5" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default LeagueSelectionGrid;